import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2'; // Import SweetAlert2 for professional alerts
import './contact.css';

// --- UTILITY SVG ICONS (Unchanged) ---
const MailIcon = ({ className, style }) => ( <svg className={className} style={style} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg> );
const PhoneIcon = ({ className, style }) => ( <svg className={className} style={style} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg> );
const ChevronDownIcon = ({ className }) => ( <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg> );
const RefreshIcon = ({ className, onClick }) => ( <svg className={className} onClick={onClick} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg> );
const SocialConnectIcon = ({ className, style }) => ( <svg className={className} style={style} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg> );
// NEW: Spinner Icon for the loading state on the button
const SpinnerIcon = () => (<svg className="contactnfc-spinner" viewBox="0 0 50 50"><circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle></svg>);

const AccordionItem = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={`contactnfc-accordion-item ${isOpen ? 'open' : ''}`}>
      <button className="contactnfc-accordion-title" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen}>
        {title}
        <ChevronDownIcon className="contactnfc-accordion-icon" />
      </button>
      {isOpen && <div className="contactnfc-accordion-content">{children}</div>}
    </div>
  );
};

const ContactNFCPage = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '', userCaptcha: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [captchaText, setCaptchaText] = useState('');
  // NEW: Add a loading state to manage form submission
  const [isLoading, setIsLoading] = useState(false);
  const MAX_MESSAGE_LENGTH = 1200;

  // NEW: Define your API URL. Use environment variables for production.
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.nownfc.com:5007/api';

  const generateCaptcha = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let c = '';
    for (let i = 0; i < 6; i++) {
      c += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(c);
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (name === 'message') {
      setMessageLength(value.length);
    }
  };

  const handleRefreshCaptcha = () => {
    generateCaptcha();
    setFormData(p => ({ ...p, userCaptcha: '' }));
  };

  // ===============================================================
  // ================== UPDATED handleSubmit Function ================
  // ===============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Frontend Validation with SweetAlert2 ---
    if (!formData.email || !formData.message) {
      Swal.fire('Incomplete Form', 'Please fill in your email and message.', 'warning');
      return;
    }
    if (formData.userCaptcha.toLowerCase() !== captchaText.toLowerCase()) {
      Swal.fire('Incorrect CAPTCHA', 'The security text does not match. Please try again.', 'error');
      handleRefreshCaptcha();
      return;
    }
    if (!termsAccepted) {
      Swal.fire('Terms Not Accepted', 'You must agree to the Privacy Policy and Terms of Service to continue.', 'warning');
      return;
    }

    setIsLoading(true);

    try {
        const response = await fetch(`${API_URL}/contactform`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                message: formData.message,
            }),
        });

        const result = await response.json();

        if (!response.ok) {
            // Throw an error with the message from the backend for the catch block
            throw new Error(result.message || 'An unknown server error occurred.');
        }

        // --- Success Alert ---
        Swal.fire({
            icon: 'success',
            title: 'Message Sent!',
            text: result.message, // Use the success message from your backend
        });
        
        // Reset form state on successful submission
        setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '', userCaptcha: '' });
        setTermsAccepted(false);
        setMessageLength(0);
        handleRefreshCaptcha();

    } catch (error) {
        // --- Error Alert ---
        Swal.fire({
            icon: 'error',
            title: 'Submission Failed',
            text: error.message || 'Could not connect to the server. Please try again later.',
        });
    } finally {
        // This will run whether the submission succeeded or failed
        setIsLoading(false);
    }
  };
  
  return (
    <div className="contactnfc-page">
      <main className="contactnfc-main-content">
        <section id="contactnfc-hero" className="contactnfc-hero-section contactnfc-container">
          {/* --- LEFT SIDE: INFO (Unchanged) --- */}
          <div className="contactnfc-hero-info">
            <h1>Contact Us</h1>
            <p className="contactnfc-subtitle">Have a question or need support? Reach out to us via email, phone, or the contact form below.</p>
            <a href="mailto:support@nownfc.com" className="contactnfc-contact-link"><MailIcon /> namaste@nownfc.com</a>
            <a href="tel:+919119115535" className="contactnfc-contact-link"><PhoneIcon /> +91-9119115535</a>
            <div className="contactnfc-info-groups">
              <div className="contactnfc-info-group"><h3>Customer Support</h3><p>Our team is ready to assist with any questions about our NFC products and services.</p></div>
              <div className="contactnfc-info-group"><h3>Feedback & Suggestions</h3><p>Your input helps us innovate. Share your thoughts on how we can improve our NFC solutions.</p></div>
              <div className="contactnfc-info-group"><h3>Partnership Inquiries</h3><p>Interested in collaborating or reselling our NFC cards? Reach out to discuss opportunities.</p></div>
            </div>
          </div>

          {/* --- RIGHT SIDE: FORM (Updated with disabled state) --- */}
          <div className="contactnfc-hero-form-wrapper">
            <div className="contactnfc-form-card">
              <h2>Get in Touch</h2>
              <p>Send us a message, and we'll respond promptly.</p>
              <form onSubmit={handleSubmit} noValidate>
                <div className="contactnfc-form-row">
                  <div className="contactnfc-form-group"><label htmlFor="contactnfc-firstName">First name</label><input type="text" id="contactnfc-firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="e.g. John" disabled={isLoading} /></div>
                  <div className="contactnfc-form-group"><label htmlFor="contactnfc-lastName">Last name</label><input type="text" id="contactnfc-lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="e.g. Doe" disabled={isLoading} /></div>
                </div>

                <div className="contactnfc-form-group"><label htmlFor="contactnfc-email">Your email *</label><div className="contactnfc-input-with-icon"><MailIcon className="contactnfc-input-icon" /><input type="email" id="contactnfc-email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" required disabled={isLoading}/></div></div>
                
                <div className="contactnfc-form-group"><label htmlFor="contactnfc-phone">Phone number</label><div className="contactnfc-phone-input-group"><span className="contactnfc-country-code">+91</span><input type="tel" id="contactnfc-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="91191 15535" disabled={isLoading} /></div></div>
                
                <div className="contactnfc-form-group"><label htmlFor="contactnfc-message">How can we help? *</label><textarea id="contactnfc-message" name="message" value={formData.message} onChange={handleChange} rows="5" maxLength={MAX_MESSAGE_LENGTH} placeholder="Tell us about your query..." required disabled={isLoading}></textarea><small className="contactnfc-char-count">{messageLength}/{MAX_MESSAGE_LENGTH}</small></div>
                
                <div className="contactnfc-form-group contactnfc-captcha-group">
                  <label htmlFor="contactnfc-userCaptcha">Security Check *</label>
                  <div className="contactnfc-captcha-row">
                    <div className="contactnfc-captcha-image-wrapper">
                      <span className="contactnfc-captcha-text">{captchaText}</span>
                      <RefreshIcon className="contactnfc-captcha-refresh-icon" onClick={handleRefreshCaptcha} />
                    </div>
                    <input type="text" id="contactnfc-userCaptcha" name="userCaptcha" value={formData.userCaptcha} onChange={handleChange} placeholder="Enter text here" required autoComplete="off" disabled={isLoading} />
                  </div>
                </div>
                
                <div className="contactnfc-form-group contactnfc-terms-group"><input type="checkbox" id="contactnfc-terms" checked={termsAccepted} onChange={() => setTermsAccepted(!termsAccepted)} required disabled={isLoading} /><label htmlFor="contactnfc-terms">By contacting us, you agree to our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.</label></div>
                
                {/* --- UPDATED: Submit button with loading state --- */}
                <button type="submit" className="contactnfc-submit-btn" disabled={isLoading}>
                    {isLoading ? <SpinnerIcon /> : 'Submit Message'}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* --- REST OF THE PAGE (Unchanged) --- */}
        <section id="contactnfc-connect" className="contactnfc-connect-section contactnfc-container">
          <div className="contactnfc-connect-header">
            <h2>Connect With Us Directly</h2>
            <p className="contactnfc-connect-subtitle">For immediate assistance or specific inquiries, feel free to use the contact methods below.</p>
          </div>
          <div className="contactnfc-connect-cards">
            <div className="contactnfc-contact-card">
                <PhoneIcon className="contactnfc-contact-card-icon" />
                <h3>Call Our Support Team</h3>
                <p><a href="tel:+919119115535">+91-9119115535</a></p>
                <p className="contactnfc-office-hours">(Mon-Sat, 9:30am - 6:30pm IST)</p>
            </div>
             <div className="contactnfc-contact-card">
                <SocialConnectIcon className="contactnfc-contact-card-icon" />
                <h3>Connect on Social</h3>
                <div className="contactnfc-social-icons">
                    <a  href="https://instagram.com/nownfc" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="contactnfc-social-link">
                        <img src="https://img.icons8.com/?size=100&id=Xy10Jcu1L2Su&format=png&color=000000" alt="WhatsApp Icon"/>
                    </a>
                    <a href="https://wa.me/919119115535" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="contactnfc-social-link">
                        <img src="https://img.icons8.com/?size=100&id=16713&format=png&color=000000" alt="Instagram Icon"/>
                    </a>
                </div>
                <p className="contactnfc-office-hours">Follow us for updates and news.</p>
            </div>
            <div className="contactnfc-contact-card">
                <MailIcon className="contactnfc-contact-card-icon" />
                <h3>Email for Inquiries</h3>
                <p><a href="mailto:namaste@nownfc.com">namaste@nownfc.com</a></p>
                <p className="contactnfc-office-hours">We typically reply within 24 hours.</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ContactNFCPage;